import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import toast from 'react-hot-toast';
import EnglishDatePicker from '../components/EnglishDatePicker/EnglishDatePicker';
import './Reports.css';
import ScreenshotService from '../services/ScreenshotService';

const Reports = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [commentText, setCommentText] = useState({});
  const [selectedReports, setSelectedReports] = useState([]);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [screenshotData, setScreenshotData] = useState(null);

  const getWorkDate = () => {
    const now = new Date();
    const cairoDate = new Date(now.toLocaleString('en-US', { timeZone: 'Africa/Cairo' }));
    const hour = cairoDate.getHours();

    if (hour < 7) {
      cairoDate.setDate(cairoDate.getDate() - 1);
    }

    const y = cairoDate.getFullYear();
    const m = String(cairoDate.getMonth() + 1).padStart(2, '0');
    const d = String(cairoDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const [filters, setFilters] = useState({
    dateFrom: getWorkDate(),
    dateTo: getWorkDate(),
    station: ''
  });

  const canAddComment = ['admin', 'engineer', 'supervisor', 'senior'].includes(user?.role);

  const translateRole = (role) => {
    const roles = {
      'engineer': 'مهندس',
      'supervisor': 'سوبر فايزر',
      'senior': 'سنيور',
      'technician': 'فني',
      'assistant': 'مساعد',
      'admin': 'مدير'
    };
    return roles[role] || role;
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const stations = [
    'المعمل المركزي + الفسطاط 17',
    'الملك الصالح 16',
    'الروضة 15',
    'ميدان الجيزة 14',
    'الجيزة 13'
  ];

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams();
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
      if (filters.station) queryParams.append('station', filters.station);

      const response = await fetch('https://metro4.dcairo.com/api/reports?' + queryParams.toString(), {
        headers: { 'Authorization': 'Bearer ' + token }
      });

      if (response.ok) {
        const data = await response.json();
        const reportsArray = data.data?.reports || data;
        const sortedReports = reportsArray.sort((a, b) => new Date(b.date) - new Date(a.date));
        setReports(sortedReports);
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const handleEditActivity = async (reportId, activityId, currentDescription) => {
    const newDescription = prompt('تعديل المهمة:', currentDescription);
    if (!newDescription || newDescription === currentDescription) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://metro4.dcairo.com/api/reports/${reportId}/activities/${activityId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({ description: newDescription })
      });

      if (response.ok) {
        toast.success('تم تعديل المهمة بنجاح');
        fetchReports();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'فشل في تعديل المهمة');
      }
    } catch (error) {
      toast.error('خطأ في الاتصال');
    }
    setLoading(false);
  };

  const handleDeleteActivity = async (reportId, activityId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه المهمة؟')) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://metro4.dcairo.com/api/reports/${reportId}/activities/${activityId}`, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + token }
      });

      if (response.ok) {
        toast.success('تم حذف المهمة بنجاح');
        fetchReports();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'فشل في حذف المهمة');
      }
    } catch (error) {
      toast.error('خطأ في الاتصال');
    }
    setLoading(false);
  };

  const handleDeleteAllActivities = async (reportId, shift) => {
    const shiftName = shift === 'نهار' ? 'النهار' : 'الليل';
    if (!window.confirm(`هل أنت متأكد من حذف كل مهام شيفت ${shiftName}؟`)) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://metro4.dcairo.com/api/reports/${reportId}/activities/deleteAll/${shift}`, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + token }
      });

      if (response.ok) {
        toast.success('تم حذف كل المهام بنجاح');
        fetchReports();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'فشل في حذف المهام');
      }
    } catch (error) {
      toast.error('خطأ في الاتصال');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReports();
  }, [filters]);

  const handleAddComment = async (reportId, shift) => {
    const commentKey = `${reportId}-${shift}`;
    const comment = commentText[commentKey]?.trim();

    if (!comment) {
      toast.error('من فضلك اكتب تعليق');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://metro4.dcairo.com/api/reports/${reportId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({ 
          comment: comment,
          shift: shift,
          addedBy: `${user.fullName} (${translateRole(user.role)})`
        })
      });

      if (response.ok) {
        toast.success('تم إضافة التعليق بنجاح');
        setCommentText({ ...commentText, [commentKey]: '' });
        fetchReports();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'فشل في إضافة التعليق');
      }
    } catch (error) {
      toast.error('خطأ في الاتصال');
    }
    setLoading(false);
  };

  const separateCommentsByShift = (comments) => {
    const dayComments = comments?.filter(c => c.shift === 'نهار') || [];
    const nightComments = comments?.filter(c => c.shift === 'ليل') || [];
    return { dayComments, nightComments };
  };

  const handleEditComment = async (reportId, activityId, currentDescription) => {
    const newDescription = prompt('تعديل التعليق:', currentDescription);
    if (!newDescription || newDescription === currentDescription) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://metro4.dcairo.com/api/reports/${reportId}/activities/${activityId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({ description: newDescription })
      });

      if (response.ok) {
        toast.success('تم تعديل التعليق بنجاح');
        fetchReports();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'فشل في تعديل التعليق');
      }
    } catch (error) {
      toast.error('خطأ في الاتصال');
    }
    setLoading(false);
  };

  const handleDeleteComment = async (reportId, activityId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا التعليق؟')) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://metro4.dcairo.com/api/reports/${reportId}/activities/${activityId}`, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + token }
      });

      if (response.ok) {
        toast.success('تم حذف التعليق بنجاح');
        fetchReports();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'فشل في حذف التعليق');
      }
    } catch (error) {
      toast.error('خطأ في الاتصال');
    }
    setLoading(false);
  };

  const canEditDeleteComment = (comment) => {
    if (!user) return false;
    // Admin و Manager يقدروا يعدلوا/يحذفوا أي comment
    if (['admin', 'manager'].includes(user.role)) return true;
    // المستخدم العادي بس comments نفسه
    return comment.createdBy === user._id;
  };

  const handleSelectReport = (reportId) => {
    if (selectedReports.includes(reportId)) {
      setSelectedReports(selectedReports.filter(id => id !== reportId));
    } else {
      setSelectedReports([...selectedReports, reportId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedReports.length === reports.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports(reports.map(r => r._id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedReports.length === 0) {
      toast.error('الرجاء تحديد تقارير للحذف');
      return;
    }

    if (!window.confirm(`هل أنت متأكد من حذف ${selectedReports.length} تقارير؟`)) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const deletePromises = selectedReports.map(reportId =>
        fetch(`https://metro4.dcairo.com/api/reports/${reportId}`, {
          method: 'DELETE',
          headers: { 'Authorization': 'Bearer ' + token }
        })
      );

      await Promise.all(deletePromises);
      toast.success('تم حذف التقارير بنجاح');
      setSelectedReports([]);
      fetchReports();
    } catch (error) {
      console.error('Error deleting reports:', error);
      toast.error('حدث خطأ أثناء الحذف');
    }
    setLoading(false);
  };

  const handleAddTaskClick = () => {
    navigate('/add-report');
  };

  const handleScreenshotClick = (reportId, type, shift, station, date) => {
    setScreenshotData({
      reportId,
      type,
      shift,
      station,
      date: new Date(date).toLocaleDateString('ar-EG')
    });
    setShowScreenshotModal(true);
  };

  const executeScreenshot = async (action) => {
    try {
      let elementId;
      const typeMap = {
        'حضور': 'attendance',
        'مهام': 'activities'
      };
      const shiftMap = {
        'نهار': 'day',
        'ليل': 'night'
      };
      
      const typeEn = typeMap[screenshotData.type];
      const shiftEn = shiftMap[screenshotData.shift];
      elementId = `${typeEn}-${shiftEn}-${screenshotData.reportId}`;
      
      await ScreenshotService.handleScreenshot(elementId, screenshotData, action);
      
      setShowScreenshotModal(false);
      toast.success('تم التقاط الصورة بنجاح');
    } catch (error) {
      console.error('Error taking screenshot:', error);
      toast.error('فشل في التقاط الصورة');
    }
  };

  const formatDateForAPI = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get last person who added activity
  const getLastActivityAuthor = (activities) => {
    if (!activities || activities.length === 0) return '';
    const lastActivity = activities[activities.length - 1];
    return lastActivity.addedBy || '';
  };

  return (
    <div className="dashboard-layout">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <div className="dashboard-content">
        <div className="dashboard-header">
          <button className="menu-toggle" onClick={toggleSidebar}>
            ☰
          </button>
          <h1>التقارير اليومية</h1>
        </div>

        <div className="dashboard-main">
          <div className="reports-container">
            <div className="reports-header">
              <h1>Daily Reports</h1>
              <button className="btn-primary" onClick={handleAddTaskClick}>
                إضافة مهام
              </button>
            </div>

            <div className="reports-list">
              <div className="filters-section">
                <h3>الفلاتر</h3>
                <div className="filters-grid">
                  <div className="filter-group">
                    <label>من تاريخ</label>
                    <EnglishDatePicker 
                      selected={filters.dateFrom ? new Date(filters.dateFrom) : null}
                      onChange={(date) => setFilters({ ...filters, dateFrom: formatDateForAPI(date) })}
                    />
                  </div>
                  <div className="filter-group">
                    <label>إلى تاريخ</label>
                    <EnglishDatePicker 
                      selected={filters.dateTo ? new Date(filters.dateTo) : null}
                      onChange={(date) => setFilters({ ...filters, dateTo: formatDateForAPI(date) })}
                    />
                  </div>
                  <div className="filter-group">
                    <label>المحطة</label>
                    <select
                      value={filters.station}
                      onChange={(e) => setFilters({ ...filters, station: e.target.value })}
                    >
                      <option value="">الكل</option>
                      {stations.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {user?.role === 'admin' && reports.length > 0 && (
                <div className="bulk-actions" style={{ 
                  padding: '15px', 
                  background: '#f8f9fa', 
                  borderRadius: '8px', 
                  marginBottom: '20px',
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={handleSelectAll}
                    style={{
                      padding: '8px 16px',
                      background: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    {selectedReports.length === reports.length ? '❌ إلغاء التحديد' : '☑️ تحديد الكل'}
                  </button>
                  {selectedReports.length > 0 && (
                    <>
                      <span style={{ color: '#666' }}>
                        محدد: {selectedReports.length} من {reports.length}
                      </span>
                      <button
                        onClick={handleDeleteSelected}
                        style={{
                          padding: '8px 16px',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer'
                        }}
                      >
                        🗑️ حذف المحدد
                      </button>
                    </>
                  )}
                </div>
              )}

              {loading ? (
                <div className="loading">جاري التحميل...</div>
              ) : reports.length === 0 ? (
                <div className="no-data">لا توجد تقارير</div>
              ) : (
                <div className="reports-grid">
				{reports.map(report => {
                    const dayActivities = report.dayActivities || [];
                    const nightActivities = report.nightActivities || [];
                    const dayAttendance = report.dayAttendance || [];
                    const nightAttendance = report.nightAttendance || [];
                    const { dayComments, nightComments } = separateCommentsByShift(report.comments);

                    return (
                      <div key={report._id} className="report-card-full" style={{ position: 'relative' }}>
                        {user?.role === 'admin' && (
                          <div style={{ 
                            position: 'absolute', 
                            top: '10px', 
                            left: '10px', 
                            zIndex: 10 
                          }}>
                            <input
                              type="checkbox"
                              checked={selectedReports.includes(report._id)}
                              onChange={() => handleSelectReport(report._id)}
                              style={{ 
                                width: '20px', 
                                height: '20px', 
                                cursor: 'pointer' 
                              }}
                            />
                          </div>
                        )}

                        {(dayActivities.length > 0 || dayAttendance.length > 0) && (
                          <>
                            {dayAttendance.length > 0 && (
                              <>
                                <div id={`attendance-day-${report._id}`}>
                                  <div className="shift-header day-shift" style={{ fontSize: '1.1rem', padding: '10px', marginTop: '0', marginBottom: '0', background: '#003DA5', color: 'white', borderRadius: '8px 8px 0 0', textAlign: 'center', fontWeight: 'bold', textShadow: '1px 1px 3px rgba(255,255,255,0.4)' }}>
                                    🏢 {report.station}<br />
                                    📅 {new Date(report.date).toLocaleDateString('en-GB')} ☀️ Day Shift
                                  </div>
                                  <div className="attendance-box day-attendance">
                                    <h4>📋 حضور شيفت نهار</h4>
                                    {dayAttendance.map((att, idx) => (
                                      <div key={idx} className="attendee-item">
                                        ☀️ {att.name}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <button
                                  className="btn-screenshot"
                                  onClick={() => handleScreenshotClick(report._id, 'حضور', 'نهار', report.station, report.date)}
                                  title="التقاط سكرين شوت"
                                  style={{ marginLeft: '20px', marginBottom: '10px' }}
                                >
                                  📸 سكرين شوت
                                </button>
                              </>
                            )}

                            {dayActivities.length > 0 && (
                              <>
                                <div id={`activities-day-${report._id}`}>
                                  <div className="shift-header day-shift" style={{ fontSize: '1.1rem', padding: '10px', marginTop: '0', marginBottom: '0', background: '#003DA5', color: 'white', borderRadius: '8px 8px 0 0', textAlign: 'center', fontWeight: 'bold', textShadow: '1px 1px 3px rgba(255,255,255,0.4)' }}>
                                    🏢 {report.station}<br />
                                    📅 {new Date(report.date).toLocaleDateString('en-GB')} ☀️ Day Shift
                                  </div>

                                  <div className="activities-table-section" style={{ marginTop: '0', paddingTop: '0', padding: '0' }}>
                                    <table className="report-activities-table" style={{ width: '100%', tableLayout: 'fixed' }}>
                                      <thead>
                                        <tr>
                                          <th style={{ width: '8%' }}>#</th>
                                          <th style={{ width: user?.role === 'admin' ? '82%' : '92%' }}>
                                            المهمة
                                            {dayActivities.length > 0 && (
                                              <div style={{ fontSize: '0.75rem', fontWeight: '500', marginTop: '5px', color: '#e8f4f8' }}>
                                                آخر كاتب: {getLastActivityAuthor(dayActivities)}
                                              </div>
                                            )}
                                          </th>
                                          {user?.role === 'admin' && (
                                            <th style={{ width: '10%', textAlign: 'center' }}>إجراءات</th>
                                          )}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {dayActivities.map((activity, index) => (
                                          <tr key={index}>
                                            <td>{index + 1}</td>
                                           <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', textAlign: 'right', padding: '10px', fontSize: '1rem', fontWeight: '700', color: '#000000', lineHeight: '1.4' }}>
                                              {activity.description}
                                            </td>
                                            {user?.role === 'admin' && (
                                              <td style={{ textAlign: 'center' }}>
                                                <button
                                                  onClick={() => handleEditActivity(report._id, activity._id, activity.description)}
                                                  style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '18px',
                                                    padding: '0 5px'
                                                  }}
                                                  title="تعديل"
                                                >
                                                  ✏️
                                                </button>
                                                <button
                                                  onClick={() => handleDeleteActivity(report._id, activity._id)}
                                                  style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '18px',
                                                    padding: '0 5px'
                                                  }}
                                                  title="حذف"
                                                >
                                                  🗑️
                                                </button>
                                              </td>
                                            )}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', marginLeft: '20px', marginTop: '10px', marginBottom: '10px' }}>
                                  <button
                                    className="btn-screenshot"
                                    onClick={() => handleScreenshotClick(report._id, 'مهام', 'نهار', report.station, report.date)}
                                    title="التقاط سكرين شوت"
                                  >
                                    📸 سكرين شوت
                                  </button>
                                  {user?.role === 'admin' && dayActivities.length > 0 && (
                                    <button
                                      onClick={() => handleDeleteAllActivities(report._id, 'نهار')}
                                      style={{
                                        padding: '8px 16px',
                                        background: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                      }}
                                      title="حذف كل المهام"
                                    >
                                      🗑️ حذف كل المهام
                                    </button>
                                  )}
                                </div>
                              </>
                            )}

                            {canAddComment && (
                              <div className="comments-section">
                                <h4>💬 إضافة تعليق على شيفت النهار</h4>
                                <textarea
                                  value={commentText[`${report._id}-نهار`] || ''}
                                  onChange={(e) => setCommentText({ 
                                    ...commentText, 
                                    [`${report._id}-نهار`]: e.target.value 
                                  })}
                                  placeholder="اكتب تعليقك هنا..."
                                  rows="3"
                                />
                                <button
                                  className="btn-add-comment"
                                  onClick={() => handleAddComment(report._id, 'نهار')}
                                  disabled={loading}
                                >
                                  ✅ إضافة تعليق
                                </button>
                              </div>
                            )}

                            {dayComments.length > 0 && (
                              <div className="comments-list">
                                <h4>💬 التعليقات - شيفت نهار</h4>
                                {dayComments.map((comment, idx) => (
                                  <div key={idx} className="comment-item">
                                    <div className="comment-text">{comment.comment}</div>
                                    <div className="comment-author">
                                      {comment.addedBy} - {new Date(comment.createdAt).toLocaleString('ar-EG')}
                                    </div>
                                    {canEditDeleteComment(comment) && (
                                      <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                        <button
                                          onClick={() => handleEditComment(report._id, comment._id, comment.comment)}
                                          style={{
                                            padding: '5px 12px',
                                            background: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                          }}
                                        >
                                          ✏️ تعديل
                                        </button>
                                        <button
                                          onClick={() => handleDeleteComment(report._id, comment._id)}
                                          style={{
                                            padding: '5px 12px',
                                            background: '#ef4444',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                          }}
                                        >
                                          🗑️ حذف
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}

                        {(dayActivities.length > 0 || dayAttendance.length > 0) && (nightActivities.length > 0 || nightAttendance.length > 0) && (
                          <div className="shift-separator">
                            <div className="red-line"></div>
                          </div>
                        )}

                        {(nightActivities.length > 0 || nightAttendance.length > 0) && (
                          <>
                            {nightAttendance.length > 0 && (
                              <>
                                <div id={`attendance-night-${report._id}`}>
                                  <div className="shift-header night-shift" style={{ fontSize: '1.1rem', padding: '10px', marginTop: '0', marginBottom: '0', background: '#1a237e', color: 'white', borderRadius: '8px 8px 0 0', textAlign: 'center', fontWeight: 'bold', textShadow: '1px 1px 3px rgba(255,255,255,0.4)' }}>
                                    🏢 {report.station}<br />
                                    📅 {new Date(report.date).toLocaleDateString('en-GB')} 🌙 Night Shift
                                  </div>
                                  <div className="attendance-box night-attendance">
                                    <h4>📋 حضور شيفت ليل</h4>
                                    {nightAttendance.map((att, idx) => (
                                      <div key={idx} className="attendee-item">
                                        🌙 {att.name}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <button
                                  className="btn-screenshot"
                                  onClick={() => handleScreenshotClick(report._id, 'حضور', 'ليل', report.station, report.date)}
                                  title="التقاط سكرين شوت"
                                  style={{ marginLeft: '20px', marginBottom: '10px' }}
                                >
                                  📸 سكرين شوت
                                </button>
                              </>
                            )}

                            {nightActivities.length > 0 && (
                              <>
                                <div id={`activities-night-${report._id}`}>
                                  <div className="shift-header night-shift" style={{ fontSize: '1.1rem', padding: '10px', marginTop: '0', marginBottom: '0', background: '#1a237e', color: 'white', borderRadius: '8px 8px 0 0', textAlign: 'center', fontWeight: 'bold', textShadow: '1px 1px 3px rgba(255,255,255,0.4)' }}>
                                    🏢 {report.station}<br />
                                    📅 {new Date(report.date).toLocaleDateString('en-GB')} 🌙 Night Shift
                                  </div>

                                  <div className="activities-table-section" style={{ marginTop: '0', paddingTop: '0', padding: '0' }}>
                                    <table className="report-activities-table" style={{ width: '100%', tableLayout: 'fixed' }}>
                                      <thead>
                                        <tr>
                                          <th style={{ width: '8%' }}>#</th>
                                          <th style={{ width: user?.role === 'admin' ? '82%' : '92%' }}>
                                            المهمة
                                            {nightActivities.length > 0 && (
                                              <div style={{ fontSize: '0.75rem', fontWeight: '500', marginTop: '5px', color: '#e8f4f8' }}>
                                                آخر كاتب: {getLastActivityAuthor(nightActivities)}
                                              </div>
                                            )}
                                          </th>
                                          {user?.role === 'admin' && (
                                            <th style={{ width: '10%', textAlign: 'center' }}>إجراءات</th>
                                          )}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {nightActivities.map((activity, index) => (
                                          <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', textAlign: 'right', padding: '10px', fontSize: '1rem', fontWeight: '700', color: '#000000', lineHeight: '1.4' }}>
                                              {activity.description}
                                            </td>
                                            {user?.role === 'admin' && (
                                              <td style={{ textAlign: 'center' }}>
                                                <button
                                                  onClick={() => handleEditActivity(report._id, activity._id, activity.description)}
                                                  style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '18px',
                                                    padding: '0 5px'
                                                  }}
                                                  title="تعديل"
                                                >
                                                  ✏️
                                                </button>
                                                <button
                                                  onClick={() => handleDeleteActivity(report._id, activity._id)}
                                                  style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '18px',
                                                    padding: '0 5px'
                                                  }}
                                                  title="حذف"
                                                >
                                                  🗑️
                                                </button>
                                              </td>
                                            )}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', marginLeft: '20px', marginTop: '10px', marginBottom: '10px' }}>
                                  <button
                                    className="btn-screenshot"
                                    onClick={() => handleScreenshotClick(report._id, 'مهام', 'ليل', report.station, report.date)}
                                    title="التقاط سكرين شوت"
                                  >
                                    📸 سكرين شوت
                                  </button>
                                  {user?.role === 'admin' && nightActivities.length > 0 && (
                                    <button
                                      onClick={() => handleDeleteAllActivities(report._id, 'ليل')}
                                      style={{
                                        padding: '8px 16px',
                                        background: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                      }}
                                      title="حذف كل المهام"
                                    >
                                      🗑️ حذف كل المهام
                                    </button>
                                  )}
                                </div>
                              </>
                            )}

                            {canAddComment && (
                              <div className="comments-section">
                                <h4>💬 إضافة تعليق على شيفت الليل</h4>
                                <textarea
                                  value={commentText[`${report._id}-ليل`] || ''}
                                  onChange={(e) => setCommentText({ 
                                    ...commentText, 
                                    [`${report._id}-ليل`]: e.target.value 
                                  })}
                                  placeholder="اكتب تعليقك هنا..."
                                  rows="3"
                                />
                                <button
                                  className="btn-add-comment"
                                  onClick={() => handleAddComment(report._id, 'ليل')}
                                  disabled={loading}
                                >
                                  ✅ إضافة تعليق
                                </button>
                              </div>
                            )}

                            {nightComments.length > 0 && (
                              <div className="comments-list">
                                <h4>💬 التعليقات - شيفت ليل</h4>
                                {nightComments.map((comment, idx) => (
                                  <div key={idx} className="comment-item">
                                    <div className="comment-text">{comment.comment}</div>
                                    <div className="comment-author">
                                      {comment.addedBy} - {new Date(comment.createdAt).toLocaleString('ar-EG')}
                                    </div>
                                    {canEditDeleteComment(comment) && (
                                      <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                        <button
                                          onClick={() => handleEditComment(report._id, comment._id, comment.comment)}
                                          style={{
                                            padding: '5px 12px',
                                            background: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                          }}
                                        >
                                          ✏️ تعديل
                                        </button>
                                        <button
                                          onClick={() => handleDeleteComment(report._id, comment._id)}
                                          style={{
                                            padding: '5px 12px',
                                            background: '#ef4444',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                          }}
                                        >
                                          🗑️ حذف
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showScreenshotModal && (
        <div className="screenshot-modal-overlay" onClick={() => setShowScreenshotModal(false)}>
          <div className="screenshot-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>📸 حفظ السكرين شوت</h3>
            <p className="screenshot-modal-info">
              {screenshotData?.type} شيفت {screenshotData?.shift}<br />
              {screenshotData?.station}<br />
              {screenshotData?.date}
            </p>
            <div className="screenshot-modal-actions">
              <button
                className="btn-screenshot-download"
                onClick={() => executeScreenshot('download')}
              >
                💾 تحميل فقط
              </button>
              <button
                className="btn-screenshot-whatsapp"
                onClick={() => executeScreenshot('both')}
              >
                📱 تحميل + واتساب
              </button>
              <button
                className="btn-screenshot-cancel"
                onClick={() => setShowScreenshotModal(false)}
              >
                ❌ إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;